import React, { forwardRef, useRef } from "react";
import { cn } from "../../lib/utils";
import { AnimatedBeam } from "../ui/animated-beam";

const Circle = forwardRef<
  HTMLDivElement,
  { className?: string; children?: React.ReactNode; style?: React.CSSProperties }
>(({ className, children, style }, ref) => {
  return (
    <div
      ref={ref}
      className={cn(
        "z-10 flex size-12 items-center justify-center rounded-full border-2 bg-white p-3 shadow-[0_0_20px_-12px_rgba(0,0,0,0.8)]",
        className,
      )}
      style={style}
    >
      {children}
    </div>
  );
});

Circle.displayName = "Circle";

const integrationData = {
  left: [
    { src: "/Images/left/magento.png", alt: "Magento" },
    { src: "/Images/left/salla.png", alt: "Salla" },
    { src: "/Images/left/shopify.png", alt: "Shopify" },
    { src: "/Images/left/woo.png", alt: "Woo" },
    { src: "/Images/left/jumia.png", alt: "Jumia" },
    { src: "/Images/left/amazon.jpeg", alt: "Amazon" },
  ],
  center: { src: "/Images/sellabroad.webp", alt: "Sellabroad" },
  right: [
    { src: "/Images/right/facebook.png", alt: "Facebook" },
    { src: "/Images/right/instagram.png", alt: "Instagram" },
    { src: "/Images/right/klaviyo.png", alt: "Klaviyo" },
    { src: "/Images/right/postscript.png", alt: "Postscript" },
    { src: "/Images/right/snapchat.png", alt: "Snapchat" },
    { src: "/Images/right/tiktok.png", alt: "Tiktok" },
  ],
};

function Integration() {
  const containerRef = useRef<HTMLDivElement>(null);
  const div1Ref = useRef<HTMLDivElement>(null);
  const div2Ref = useRef<HTMLDivElement>(null);
  const div3Ref = useRef<HTMLDivElement>(null);
  const div4Ref = useRef<HTMLDivElement>(null);
  const div5Ref = useRef<HTMLDivElement>(null);
  const div6Ref = useRef<HTMLDivElement>(null);
  const div7Ref = useRef<HTMLDivElement>(null);
  const div8Ref = useRef<HTMLDivElement>(null);
  const div9Ref = useRef<HTMLDivElement>(null);
  const div10Ref = useRef<HTMLDivElement>(null);
  const div11Ref = useRef<HTMLDivElement>(null);
  const div12Ref = useRef<HTMLDivElement>(null);
  const div13Ref = useRef<HTMLDivElement>(null);

  const allRefs = [div1Ref, div2Ref, div3Ref, div4Ref, div5Ref, div6Ref];
  const rightRefs = [div8Ref, div9Ref, div10Ref, div11Ref, div12Ref, div13Ref];

  return (
    <div className="py-12 bg-white">
      <div className="text-center mb-10">
        <p className="text-base font-semibold text-[#0049ac]">Integration</p>
        <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
          Seamless Integration
        </h2>
        <p className="mt-4 text-xl text-gray-600">
          Connect with leading e-commerce platforms and marketing channels
        </p>
      </div>

      {/* Mobile Layout */}
      <div className="xl:hidden px-4">
        <div className="flex flex-col gap-8">
          {/* Top section - Left images */}
          <div className="grid grid-cols-3 gap-4 justify-items-center">
            {integrationData.left.map((item, index) => (
              <div key={`left-${index}`} className="w-20 h-20 flex items-center justify-center">
                <Circle style={{ width: '80px', height: '80px' }}>
                  <img
                    src={item.src}
                    alt={item.alt}
                    className="w-16 h-16 object-contain"
                  />
                </Circle>
              </div>
            ))}
          </div>

          {/* Middle section - Center image */}
          <div className="flex justify-center">
            <Circle style={{ width: '100px', height: '100px' }}>
              <img
                src={integrationData.center.src}
                alt={integrationData.center.alt}
                className="w-20 h-20 object-contain"
              />
            </Circle>
          </div>

          {/* Bottom section - Right images */}
          <div className="grid grid-cols-3 gap-4 justify-items-center">
            {integrationData.right.map((item, index) => (
              <div key={`right-${index}`} className="w-20 h-20 flex items-center justify-center">
                <Circle style={{ width: '80px', height: '80px' }}>
                  <img
                    src={item.src}
                    alt={item.alt}
                    className="w-16 h-16 object-contain"
                  />
                </Circle>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Desktop Layout with Animations */}
      <div
        className="relative hidden xl:flex h-[800px] w-full max-w-[1300px] mx-auto items-center justify-center overflow-hidden rounded-lg border bg-background p-10 md:shadow-xl"
        ref={containerRef}
      >
        <div className="flex size-full flex-col max-w-lg max-h-[400px] items-stretch justify-center gap-12">
          <div className="flex flex-row items-center justify-center">
            {/* Left Column */}
            <div className="flex flex-col items-center">
              {integrationData.left.map((item, index) => (
                <Circle
                  key={`desktop-left-${index}`}
                  ref={allRefs[index]}
                  className={`mb-${index === 5 ? '0' : '6'}`}
                  style={{ width: '80px', height: '80px' }}
                >
                  <img
                    src={item.src}
                    alt={item.alt}
                    className="w-24 h-24 object-contain"
                  />
                </Circle>
              ))}
            </div>

            {/* Center */}
            <div className="flex flex-col items-center" style={{ margin: '26rem' }}>
              <Circle
                ref={div7Ref}
                className="mx-auto"
                style={{ width: '100px', height: '100px' }}
              >
                <img
                  src={integrationData.center.src}
                  alt={integrationData.center.alt}
                  className="w-24 h-24 object-contain"
                />
              </Circle>
            </div>

            {/* Right Column */}
            <div className="flex flex-col items-center">
              {integrationData.right.map((item, index) => (
                <Circle
                  key={`desktop-right-${index}`}
                  ref={rightRefs[index]}
                  className={`mb-${index === 5 ? '0' : '6'}`}
                  style={{ width: '80px', height: '80px' }}
                >
                  <img
                    src={item.src}
                    alt={item.alt}
                    className="w-24 h-24 object-contain"
                  />
                </Circle>
              ))}
            </div>
          </div>
        </div>

        {/* Animated Beams */}
        {allRefs.map((ref, index) => (
          <AnimatedBeam
            key={`beam-left-${index}`}
            containerRef={containerRef}
            fromRef={div7Ref}
            toRef={ref}
            curvature={index % 2 === 0 ? -75 : 75}
            endYOffset={index % 2 === 0 ? -10 : 10}
            reverse={index > 2}
          />
        ))}
        {rightRefs.map((ref, index) => (
          <AnimatedBeam
            key={`beam-right-${index}`}
            containerRef={containerRef}
            fromRef={div7Ref}
            toRef={ref}
            curvature={index % 2 === 0 ? -75 : 75}
            endYOffset={index % 2 === 0 ? -10 : 10}
            reverse={index > 2}
          />
        ))}
      </div>
    </div>
  );
}

export default Integration;